import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { api } from '@shared/services/api';
import { Card, Badge, Button } from '@shared/components/ui';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  Line,
  Legend,
} from 'recharts';
import { Cpu, Download, HardDrive, RefreshCw, TrendingUp, Zap } from 'lucide-react';
import { useTheme } from '@shared/contexts/ThemeContext';
import { vizColor } from '@shared/utils/statusTone';
import { useTranslation } from 'react-i18next';
import { formatMetricBucketLabel } from '../utils/formatMetricBucket';
import { formatMetricValue } from '../utils/formatMetricValue';

interface AgentMetrics {
  agent_id: string;
  cpu_metrics: Array<{ timestamp: string; usage: number }>;
  memory_metrics: Array<{ timestamp: string; percent: number; total: number; used: number }>;
  disk_metrics: Array<{ timestamp: string; percent: number; total: number; used: number }>;
  io_metrics: Array<{
    timestamp: string;
    read_mb_s: number;
    write_mb_s: number;
    iops: number;
    latency_ms: number;
  }>;
  current_cpu_usage: number | null;
  current_memory_percent: number | null;
  current_disk_percent: number | null;
  current_io_read_mb_s: number | null;
  current_io_write_mb_s: number | null;
  hours: number;
}

interface Agent {
  id: string;
  name: string;
}

export default function Metrics() {
  const { t, i18n } = useTranslation('dashboard');
  const locale = i18n.resolvedLanguage || i18n.language || 'en';
  const { theme } = useTheme();
  const [metrics, setMetrics] = useState<AgentMetrics | null>(null);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<'agents' | 'metrics' | null>(null);
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [chartInitialDimension, setChartInitialDimension] = useState<{
    width: number;
    height: number;
  }>();

  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [metricType, setMetricType] = useState('ALL');
  const [hours, setHours] = useState('168');

  useEffect(() => {
    fetchAgents();
  }, []);

  useEffect(() => {
    if (selectedAgentId) fetchMetrics();
  }, [selectedAgentId, metricType, hours]);

  const fetchAgents = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<Agent[]>('/agents');
      const list = Array.isArray(res.data) ? res.data : [];
      setAgents(list);
      if (list.length > 0 && !selectedAgentId) setSelectedAgentId(list[0].id);
    } catch {
      setAgents([]);
      setError('agents');
    } finally {
      setLoading(false);
    }
  };

  const fetchMetrics = async () => {
    if (!selectedAgentId) return;

    setLoading(true);
    setError(null);
    setMetrics(null);
    try {
      const res = await api.get<AgentMetrics>(`/agents/${selectedAgentId}/metrics?hours=${hours}`);
      setMetrics(res.data);
    } catch {
      setError('metrics');
    } finally {
      setLoading(false);
    }
  };

  const chartData = useMemo(() => {
    if (!metrics) return [];

    const getBucket = (ts: string) => {
      const d = new Date(ts);
      const bucketSize = Number(hours) <= 24 ? 60 : Number(hours) <= 168 ? 300 : 1800;
      const seconds = d.getUTCHours() * 3600 + d.getUTCMinutes() * 60 + d.getUTCSeconds();
      const bucketStart = Math.floor(seconds / bucketSize) * bucketSize;
      const dateStr = d.toISOString().slice(0, 11);
      const bh = Math.floor(bucketStart / 3600);
      const bm = Math.floor((bucketStart % 3600) / 60);
      return `${dateStr}${String(bh).padStart(2, '0')}:${String(bm).padStart(2, '0')}:00`;
    };

    const groups = new Map<
      string,
      {
        cpu: number[];
        memory: number[];
        disk: number[];
        io: number[];
        label: string;
      }
    >();

    const getGroup = (bucket: string) => {
      if (!groups.has(bucket)) {
        groups.set(bucket, {
          cpu: [],
          memory: [],
          disk: [],
          io: [],
          label: formatMetricBucketLabel(bucket, Number(hours), locale),
        });
      }
      return groups.get(bucket)!;
    };

    const showCPU = metricType === 'ALL' || metricType === 'CPU';
    const showMemory = metricType === 'ALL' || metricType === 'MEMORY';
    const showDisk = metricType === 'ALL' || metricType === 'DISK';
    const showIO = metricType === 'ALL' || metricType === 'IO';

    if (showCPU)
      metrics.cpu_metrics.forEach((m) => {
        getGroup(getBucket(m.timestamp)).cpu.push(m.usage);
      });
    if (showMemory)
      metrics.memory_metrics.forEach((m) => {
        getGroup(getBucket(m.timestamp)).memory.push(m.percent);
      });
    if (showDisk)
      metrics.disk_metrics.forEach((m) => {
        getGroup(getBucket(m.timestamp)).disk.push(m.percent);
      });
    if (showIO)
      metrics.io_metrics.forEach((m) => {
        getGroup(getBucket(m.timestamp)).io.push((m.read_mb_s ?? 0) + (m.write_mb_s ?? 0));
      });

    const avg = (arr: number[]) =>
      arr.length ? Math.round((arr.reduce((a, b) => a + b, 0) / arr.length) * 100) / 100 : 0;

    return Array.from(groups.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([, g]) => ({
        time: g.label,
        cpu: showCPU ? avg(g.cpu) : undefined,
        memory: showMemory ? avg(g.memory) : undefined,
        disk: showDisk ? avg(g.disk) : undefined,
        io: showIO ? avg(g.io) : undefined,
      }));
  }, [metrics, metricType, hours, locale]);

  const tableRows = useMemo(() => {
    if (!metrics) return [];

    const showCPU = metricType === 'ALL' || metricType === 'CPU';
    const showMemory = metricType === 'ALL' || metricType === 'MEMORY';
    const showDisk = metricType === 'ALL' || metricType === 'DISK';
    const showIO = metricType === 'ALL' || metricType === 'IO';

    type Row = {
      timestamp: string;
      type: string;
      icon: string;
      value: string;
      percent: number | null;
    };
    const rows: Row[] = [];

    if (showCPU)
      metrics.cpu_metrics.forEach((m) => {
        rows.push({
          timestamp: m.timestamp,
          type: 'CPU',
          icon: 'cpu',
          value: `${formatMetricValue(m.usage, locale)}%`,
          percent: m.usage,
        });
      });
    if (showMemory)
      metrics.memory_metrics.forEach((m) => {
        rows.push({
          timestamp: m.timestamp,
          type: 'MEMORY',
          icon: 'memory',
          value: `${formatMetricValue(m.percent, locale)}%`,
          percent: m.percent,
        });
      });
    if (showDisk)
      metrics.disk_metrics.forEach((m) => {
        rows.push({
          timestamp: m.timestamp,
          type: 'DISK',
          icon: 'disk',
          value: `${formatMetricValue(m.percent, locale)}%`,
          percent: m.percent,
        });
      });
    if (showIO)
      metrics.io_metrics.forEach((m) => {
        rows.push({
          timestamp: m.timestamp,
          type: 'IO',
          icon: 'io',
          value: `R: ${formatMetricValue(m.read_mb_s ?? 0, locale)} W: ${formatMetricValue(m.write_mb_s ?? 0, locale)} MB/s`,
          percent: null,
        });
      });

    rows.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
    return rows;
  }, [metrics, metricType, locale]);

  const exportMetrics = () => {
    if (tableRows.length === 0) return;

    const escapeCsvCell = (value: string) => `"${value.replace(/"/g, '""')}"`;
    const csvRows = [
      [
        t('systemMetrics.timestamp'),
        t('systemMetrics.resource'),
        t('systemMetrics.telemetryValue'),
      ],
      ...tableRows.map((row) => [
        new Date(row.timestamp).toLocaleString(locale),
        row.type === 'IO'
          ? t('systemMetrics.diskIo')
          : t(`systemMetrics.types.${row.type.toLowerCase()}`),
        row.value,
      ]),
    ];
    const csv = `\uFEFF${csvRows.map((row) => row.map(escapeCsvCell).join(',')).join('\r\n')}`;
    const blobUrl = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    const agentName = agents.find((agent) => agent.id === selectedAgentId)?.name ?? 'agent';
    const filenameAgent = agentName
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .toLowerCase();

    link.href = blobUrl;
    link.download = `pgkb-metrics-${filenameAgent || 'agent'}-${hours}h.csv`;
    link.click();
    window.setTimeout(() => URL.revokeObjectURL(blobUrl), 0);
  };

  const hasData = tableRows.length > 0;
  const visibleTableRows = tableRows.slice(-20).reverse();
  const getMetricTypeLabel = (type: string) =>
    type === 'IO' ? t('systemMetrics.diskIo') : t(`systemMetrics.types.${type.toLowerCase()}`);

  useLayoutEffect(() => {
    if (!hasData) {
      setChartInitialDimension(undefined);
      return;
    }

    const { width, height } = chartContainerRef.current?.getBoundingClientRect() ?? {
      width: 0,
      height: 0,
    };
    if (width <= 0 || height <= 0) return;

    const nextDimension = { width: Math.round(width), height: Math.round(height) };
    setChartInitialDimension((current) =>
      current?.width === nextDimension.width && current.height === nextDimension.height
        ? current
        : nextDimension
    );
  }, [hasData]);

  // Series colors — shared viz contract (viz-1..4), same mapping as AgentDetails.
  const cpuColor = vizColor('cpu', theme);
  const ramColor = vizColor('ram', theme);
  const diskColor = vizColor('disk', theme);
  const ioColor = vizColor('io', theme);
  const ioLabel = t('systemMetrics.ioMbPerSecond');

  return (
    <>
      <div className="space-y-8 animate-in fade-in duration-300">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h1 className="app-page-title">{t('systemMetrics.title')}</h1>
            <p className="app-page-subtitle">{t('systemMetrics.subtitle')}</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void fetchMetrics()}
              disabled={!selectedAgentId || loading}
              aria-label={t('systemMetrics.refresh')}
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            </Button>
            <Button
              size="sm"
              onClick={exportMetrics}
              disabled={tableRows.length === 0}
              title={tableRows.length === 0 ? t('systemMetrics.noDataToExport') : undefined}
            >
              <Download size={16} className="mr-2" /> {t('systemMetrics.export')}
            </Button>
          </div>
        </div>

        <Card className="p-4 flex flex-col lg:flex-row gap-4 shadow-soft">
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            <div className="space-y-1">
              <label htmlFor="metrics-agent" className="app-field-label">
                {t('systemMetrics.targetAgent')}
              </label>
              <select
                id="metrics-agent"
                value={selectedAgentId}
                onChange={(e) => setSelectedAgentId(e.target.value)}
                className="app-field-control w-full"
              >
                {agents.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label htmlFor="metrics-type" className="app-field-label">
                {t('systemMetrics.metricType')}
              </label>
              <select
                id="metrics-type"
                value={metricType}
                onChange={(e) => setMetricType(e.target.value)}
                className="app-field-control w-full"
              >
                <option value="ALL">{t('systemMetrics.allMetrics')}</option>
                <option value="CPU">{t('labels.cpuUsage')}</option>
                <option value="MEMORY">{t('labels.memory')}</option>
                <option value="DISK">{t('systemMetrics.diskUsage')}</option>
                <option value="IO">{t('systemMetrics.diskIo')}</option>
              </select>
            </div>
            <div className="space-y-1">
              <label htmlFor="metrics-time-range" className="app-field-label">
                {t('systemMetrics.timeRange')}
              </label>
              <select
                id="metrics-time-range"
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                className="app-field-control w-full"
              >
                <option value="24">{t('timeRanges.last24Hours')}</option>
                <option value="168">{t('timeRanges.last7Days')}</option>
                <option value="720">{t('timeRanges.last30Days')}</option>
              </select>
            </div>
          </div>
        </Card>

        {error ? (
          <div
            role="alert"
            className="rounded-xl border border-state-danger-light/20 dark:border-state-danger-dark/20 bg-state-danger-bg-light dark:bg-state-danger-bg-dark p-4 text-center text-state-danger-light dark:text-state-danger-dark"
          >
            <p className="font-semibold">
              {t(
                error === 'agents'
                  ? 'systemMetrics.errors.loadAgents'
                  : 'systemMetrics.errors.loadMetrics'
              )}
            </p>
            <Button
              variant="secondary"
              className="mt-3"
              onClick={() => void (error === 'agents' ? fetchAgents() : fetchMetrics())}
            >
              {t('systemMetrics.retry')}
            </Button>
          </div>
        ) : loading ? (
          <div className="py-24 flex justify-center">
            <div className="animate-spin h-12 w-12 border-b-2 border-brand-600 rounded-full"></div>
          </div>
        ) : hasData ? (
          <div className="space-y-8">
            <Card className="p-8">
              <div className="mb-8 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
                <h3 className="flex min-w-0 items-center gap-2 text-lg font-black uppercase tracking-widest text-slate-900 dark:text-white">
                  <TrendingUp className="text-brand-600 dark:text-brand-400" />{' '}
                  {t('analytics.resourceUtilization')}
                </h3>
                <Badge variant="info" className="shrink-0">
                  {t('labels.telemetryHistory')}
                </Badge>
              </div>
              <div ref={chartContainerRef} className="h-[400px] w-full">
                {chartInitialDimension && (
                  <ResponsiveContainer
                    width="100%"
                    height="100%"
                    initialDimension={chartInitialDimension}
                  >
                    <AreaChart data={chartData}>
                      <defs>
                        <linearGradient id="colorCpu" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={cpuColor} stopOpacity={0.1} />
                          <stop offset="95%" stopColor={cpuColor} stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="colorMemory" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={ramColor} stopOpacity={0.1} />
                          <stop offset="95%" stopColor={ramColor} stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="colorDisk" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={diskColor} stopOpacity={0.1} />
                          <stop offset="95%" stopColor={diskColor} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        vertical={false}
                        stroke="currentColor"
                        className="text-surface-border-light dark:text-surface-border-dark"
                      />
                      <XAxis dataKey="time" hide />
                      <YAxis hide yAxisId="left" domain={[0, 100]} />
                      <YAxis hide yAxisId="right" orientation="right" domain={[0, 'auto']} />
                      <Legend
                        verticalAlign="top"
                        height={20}
                        iconType="circle"
                        wrapperStyle={{ fontSize: '11px', paddingBottom: '10px' }}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'var(--tooltip-bg)',
                          border: 'none',
                          borderRadius: '8px',
                          color: 'var(--tooltip-text)',
                          fontSize: '12px',
                        }}
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        formatter={(value: any, name: any) => {
                          const v = Number(value);
                          if (name === ioLabel)
                            return [`${formatMetricValue(v, locale)} MB/s`, name];
                          return [`${formatMetricValue(v, locale)}%`, name];
                        }}
                      />
                      {(metricType === 'ALL' || metricType === 'CPU') && (
                        <Area
                          type="monotone"
                          dataKey="cpu"
                          name="CPU"
                          stroke={cpuColor}
                          fillOpacity={1}
                          fill="url(#colorCpu)"
                          strokeWidth={2}
                          yAxisId="left"
                        />
                      )}
                      {(metricType === 'ALL' || metricType === 'MEMORY') && (
                        <Area
                          type="monotone"
                          dataKey="memory"
                          name={t('labels.memory')}
                          stroke={ramColor}
                          fillOpacity={1}
                          fill="url(#colorMemory)"
                          strokeWidth={2}
                          yAxisId="left"
                        />
                      )}
                      {(metricType === 'ALL' || metricType === 'DISK') && (
                        <Area
                          type="monotone"
                          dataKey="disk"
                          name={t('systemMetrics.disk')}
                          stroke={diskColor}
                          fillOpacity={1}
                          fill="url(#colorDisk)"
                          strokeWidth={2}
                          yAxisId="left"
                        />
                      )}
                      {(metricType === 'ALL' || metricType === 'IO') && (
                        <Line
                          type="monotone"
                          dataKey="io"
                          name={ioLabel}
                          stroke={ioColor}
                          strokeWidth={2}
                          dot={false}
                          yAxisId="right"
                        />
                      )}
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </div>
            </Card>

            <div className="bg-surface-card-light dark:bg-surface-card-dark rounded-2xl shadow-soft border border-surface-border-light dark:border-surface-border-dark overflow-hidden">
              <div className="divide-y divide-surface-border-light dark:divide-surface-border-dark sm:hidden">
                {visibleTableRows.map((row) => {
                  const metricLabel = getMetricTypeLabel(row.type);

                  return (
                    <article key={`${row.type}-${row.timestamp}`} className="space-y-3 p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex min-w-0 items-start gap-2">
                          <span className="shrink-0 rounded-lg bg-accent-soft-light p-1.5 text-brand-600 dark:bg-accent-soft-dark dark:text-brand-400">
                            {row.icon === 'cpu' ? (
                              <Cpu size={14} />
                            ) : row.icon === 'disk' || row.icon === 'io' ? (
                              <HardDrive size={14} />
                            ) : (
                              <Zap size={14} />
                            )}
                          </span>
                          <div className="min-w-0">
                            <p className="break-words text-sm font-semibold text-slate-900 dark:text-white">
                              {metricLabel}
                            </p>
                            <time
                              dateTime={row.timestamp}
                              className="mt-1 block text-xs text-text-subtle-light dark:text-text-subtle-dark"
                            >
                              {new Date(row.timestamp).toLocaleString(locale)}
                            </time>
                          </div>
                        </div>
                        <div className="max-w-[52%] shrink-0 text-right">
                          <p className="text-[10px] text-text-subtle-light dark:text-text-subtle-dark">
                            {t('systemMetrics.telemetryValue')}
                          </p>
                          <p className="mt-1 break-words text-sm font-bold text-slate-900 dark:text-white">
                            {row.value}
                          </p>
                        </div>
                      </div>
                      {row.percent != null && (
                        <div
                          role="progressbar"
                          aria-label={`${metricLabel}: ${row.value}`}
                          aria-valuemin={0}
                          aria-valuemax={100}
                          aria-valuenow={row.percent}
                          className="h-1.5 overflow-hidden rounded-full bg-surface-2-light dark:bg-surface-2-dark"
                        >
                          <div
                            className="h-full rounded-full bg-brand-500"
                            style={{ width: `${row.percent}%` }}
                          />
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>
              <div className="hidden overflow-x-auto sm:block">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-surface-border-light dark:border-surface-border-dark bg-surface-2-light/50 dark:bg-surface-2-dark/50">
                      <th className="p-4 text-[10px] font-black uppercase tracking-widest text-text-subtle-light dark:text-text-subtle-dark">
                        {t('systemMetrics.timestamp')}
                      </th>
                      <th className="p-4 text-[10px] font-black uppercase tracking-widest text-text-subtle-light dark:text-text-subtle-dark">
                        {t('systemMetrics.resource')}
                      </th>
                      <th className="p-4 text-[10px] font-black uppercase tracking-widest text-text-subtle-light dark:text-text-subtle-dark">
                        {t('systemMetrics.telemetryValue')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-surface-border-light dark:divide-surface-border-dark">
                    {visibleTableRows.map((m) => (
                      <tr
                        key={`${m.type}-${m.timestamp}`}
                        className="hover:bg-surface-2-light dark:hover:bg-surface-2-dark/30 transition-colors"
                      >
                        <td className="p-4">
                          <div className="text-sm font-bold text-slate-700 dark:text-slate-300">
                            {new Date(m.timestamp).toLocaleDateString(locale)}
                          </div>
                          <div className="text-[10px] text-text-subtle-light dark:text-text-subtle-dark font-mono uppercase">
                            {new Date(m.timestamp).toLocaleTimeString(locale)}
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <span className="p-1.5 rounded-lg bg-accent-soft-light dark:bg-accent-soft-dark text-brand-600 dark:text-brand-400">
                              {m.icon === 'cpu' ? (
                                <Cpu size={14} />
                              ) : m.icon === 'disk' || m.icon === 'io' ? (
                                <HardDrive size={14} />
                              ) : (
                                <Zap size={14} />
                              )}
                            </span>
                            <span className="text-[10px] font-black text-slate-900 dark:text-white uppercase tracking-widest">
                              {getMetricTypeLabel(m.type)}
                            </span>
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            {m.percent != null ? (
                              <>
                                <div className="w-24 bg-surface-2-light dark:bg-surface-2-dark h-1.5 rounded-full overflow-hidden">
                                  <div
                                    className="bg-brand-500 h-full rounded-full shadow-lg shadow-brand-200 dark:shadow-none"
                                    style={{ width: `${m.percent}%` }}
                                  />
                                </div>
                                <span className="text-sm font-black text-slate-900 dark:text-white">
                                  {m.value}
                                </span>
                              </>
                            ) : (
                              <span className="text-sm font-black text-slate-900 dark:text-white">
                                {m.value}
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : agents.length === 0 ? (
          <div className="py-24 flex flex-col items-center justify-center text-text-subtle-light dark:text-text-subtle-dark">
            <TrendingUp size={40} className="mb-2 opacity-20" />
            <p className="text-sm font-semibold">{t('systemMetrics.noAgents')}</p>
          </div>
        ) : (
          <div className="py-24 flex flex-col items-center justify-center text-text-subtle-light dark:text-text-subtle-dark">
            <TrendingUp size={40} className="mb-2 opacity-20" />
            <p className="text-sm font-semibold">{t('systemMetrics.noDataForFilter')}</p>
          </div>
        )}
      </div>
    </>
  );
}
